import { useEffect } from 'react';
import jwtDecode from 'jwt-decode';

import useAuth from './useAuth';
import { useRefreshTokens } from '../services/auth/authServices';

const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    // Check if token will expire in next 30 seconds
    return (decoded.exp * 1000) <= (Date.now() + 30000);
  } catch {
    return true;
  }
};

// Shared refresh promise to prevent multiple simultaneous refreshes
let refreshPromise = null;

// This custom hook is responsible for adding an authorization header to Axios requests
// using the user's access token. It also refreshes the access token if it has expired
// and the user has a refresh token.
const useAxiosPrivate = (axiosInstance) => {
  const refresh = useRefreshTokens();
  const { auth, setAuth } = useAuth();

  useEffect(() => {
    const getRefreshedConfig = async (config) => {
      // If there's already a refresh in progress, wait for it
      if (refreshPromise) {
        const tokens = await refreshPromise;
        if (tokens?.accessToken) {
          return {
            ...config,
            headers: {
              ...config.headers,
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          };
        }
        return config;
      }

      // Start a new refresh
      refreshPromise = refresh();
      try {
        const newTokens = await refreshPromise;
        if (newTokens) {
          setAuth((prev) => ({
            ...prev,
            accessToken: newTokens.accessToken,
          }));
          return {
            ...config,
            headers: {
              ...config.headers,
              Authorization: `Bearer ${newTokens.accessToken}`,
            },
          };
        }
      } finally {
        refreshPromise = null;
      }
      return config;
    };

    // Add a request interceptor to set the authorization header on outgoing requests.
    const requestIntercept = axiosInstance.interceptors.request.use(
      async (config) => {
        const newConfig = { ...config };
        if (!newConfig.headers.Authorization) {
          if (auth?.accessToken && !isTokenExpired(auth?.accessToken)) {
            // Token exists and is not expired, use it
            newConfig.headers.Authorization = `Bearer ${auth.accessToken}`;
          } else {
            // Token is undefined or expired, refresh it proactively
            return getRefreshedConfig(newConfig);
          }
        }
        return newConfig;
      },
      (error) => Promise.reject(error),
    );

    // Add a response interceptor to handle 403 (Forbidden) errors and refresh the access token.
    const responseIntercept = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 403 && !prevRequest?.sent) {
          prevRequest.sent = true;
          const newTokens = await refresh();
          if (!newTokens) return Promise.reject(error);
          prevRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return axiosInstance(prevRequest);
        }
        return Promise.reject(error);
      },
    );

    // Clean up the request and response interceptors when the component unmounts.
    return () => {
      axiosInstance.interceptors.request.eject(requestIntercept);
      axiosInstance.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh, setAuth]);

  // Return the axios instance with the added interceptors and authorization header.
  return axiosInstance;
};

export default useAxiosPrivate;
